import React, { useState } from 'react';
import { authService } from '../services/authService';
import '../styles/AuthModal.css';

export function AuthModal({ isOpen, onClose, defaultTab = 'login', onLoginSuccess }) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [signupEmailSent, setSignupEmailSent] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (activeTab === 'login') {
        const result = await authService.login(formData.email, formData.password);
        if (result.success) {
          await onLoginSuccess?.(result.user);
          onClose();
        }
      } else {
        const result = await authService.signup(formData.email, formData.password, formData.name);
        if (result.success) {
          setSignupEmailSent(true);
        }
      }
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'EMAIL_NOT_VERIFIED') {
        setNeedsVerification(true);
      }
      setError(err.response?.data?.message || err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await authService.getGoogleAuthUrl();
      if (result.success && result.url) {
        // Redirect to Google OAuth
        window.location.href = result.url;
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to initiate Google login');
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await authService.triggerResetPassword(formData.email);
      if (result.success) {
        setResetEmailSent(true);
      } else {
        setError(result.message || 'Failed to send reset email');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setShowForgotPassword(false);
    setResetEmailSent(false);
    setSignupEmailSent(false);
    setNeedsVerification(false);
    setError(null);
    setFormData({ name: '', email: '', password: '' });
  };

  const openForgotPassword = () => {
    setShowForgotPassword(true);
    setResetEmailSent(false);
    setSignupEmailSent(false);
    setNeedsVerification(false);
    setError(null);
  };

  const backToLogin = () => {
    setShowForgotPassword(false);
    setResetEmailSent(false);
    setSignupEmailSent(false);
    setNeedsVerification(false);
    setActiveTab('login');
    setError(null);
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setError('Enter your email to resend the verification link.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await authService.resendVerification(formData.email);
      if (result.success) {
        setSignupEmailSent(true);
        setNeedsVerification(false);
      } else {
        setError(result.message || 'Failed to send verification email');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to send verification email');
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="auth-modal-overlay" onClick={onClose}>
        <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
          <button className="auth-modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <div className="auth-modal-header">
            <h2>Reset Your Password</h2>
            <p>We'll send you a link to reset your password</p>
          </div>

          {resetEmailSent ? (
            <div className="reset-success">
              <div className="success-icon">✓</div>
              <h3>Check Your Email</h3>
              <p>We've sent a password reset link to {formData.email}</p>
              <button className="btn btn-primary btn-full" onClick={backToLogin}>
                Back to Login
              </button>
            </div>
          ) : (
            <form className="auth-form" onSubmit={handleForgotPasswordSubmit}>
              <div className="form-group">
                <label htmlFor="reset-email">Email Address</label>
                <input
                  type="email"
                  id="reset-email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              {error && (
                <div className="auth-error">
                  {error}
                </div>
              )}

              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>

              <button type="button" className="btn-back" onClick={backToLogin}>
                ← Back to Login
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div className="auth-modal-header">
          <h2>Welcome to Get Fit with Daniella</h2>
          <p>Start your fitness journey today</p>
        </div>

        <div className="auth-tabs">
          <button
            className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => switchTab('login')}
          >
            Login
          </button>
          <button
            className={`auth-tab ${activeTab === 'signup' ? 'active' : ''}`}
            onClick={() => switchTab('signup')}
          >
            Sign Up
          </button>
        </div>

        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}

        {signupEmailSent ? (
          <div className="reset-success">
            <div className="success-icon">✓</div>
            <h3>Check Your Email</h3>
            <p>We've sent a verification link to {formData.email}. Verify your email before logging in.</p>
            <button className="btn btn-primary btn-full" onClick={() => switchTab('login')}>
              Back to Login
            </button>
          </div>
        ) : (
          <>
        <form className="auth-form" onSubmit={handleSubmit}>
          {activeTab === 'signup' && (
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleChange}
              required
                  minLength={8}
            />
          </div>

          {activeTab === 'login' && (
            <div className="form-options">
              <label className="remember-me">
                <input type="checkbox" />
                <span>Remember me</span>
              </label>
              <button type="button" className="forgot-password-btn" onClick={openForgotPassword}>
                Forgot password?
              </button>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={loading}
          >
            {loading ? 'Please wait...' : activeTab === 'login' ? 'Login' : 'Sign Up'}
          </button>
        </form>

        {needsVerification && (
          <button
            type="button"
            className="btn-back"
            onClick={handleResendVerification}
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Resend verification email'}
          </button>
        )}

        <div className="auth-divider">
          <span>or continue with</span>
        </div>

        <div className="social-login">
          <button
            className="social-btn google"
            onClick={handleGoogleLogin}
            disabled={loading}
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {loading ? 'Please wait...' : 'Continue with Google'}
          </button>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
